import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { v4 as uuid } from 'uuid';
import PropTypes from 'prop-types';
import { debounce } from '../../../helpers/debounce';
import AdvancedSelect from '../../atoms/AdvancedSelect/AdvancedSelect';
import { Field } from 'redux-form';
import { get } from 'object-path';
import FormField from '../../atoms/Form/Field/FormField';
import FieldError from '../Error/FieldError';
import useTranslate from '../../../hooks/useTranslate';
import { useActions } from '../../../hooks/useActions';
import { fetchOptions as fetchOptionsAction } from '../../../store/actions/options';
import { useSelector } from 'react-redux';
import { optionsStateSelector, optionsErrorSelector } from '../../../store/selectors/options';
import { getConfidenceFieldStyle } from '../../../helpers/form';
import { fetchRemoteOptionsFromSettings } from '../../../store/middleware/options';

const AdvancedSelectFieldRender = ({
  input,
  meta: { error, touched },
  placeholder,
  disabled,
  confidence,
  ...props
}) => {
  const i18n = useTranslate();
  const placeholderMemo = useMemo(() => {
    return placeholder || placeholder === ''
      ? placeholder
      : i18n('AdvancedSelectField.placeholder');
  }, [placeholder]);

  const style = useMemo(() => {
    if (touched) return null;
    return getConfidenceFieldStyle(confidence);
  }, [confidence, touched]);

  return (
    <>
      <AdvancedSelect
        disabled={disabled}
        error={!!(touched && error)}
        {...input}
        placeholder={placeholderMemo}
        style={style}
        {...props}
      />
      {touched && error && <FieldError error={error} />}
    </>
  );
};

AdvancedSelectFieldRender.propTypes = {
  input: PropTypes.object,
  meta: PropTypes.object,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  confidence: PropTypes.number,
};

const getRemoteOptionTuple = async (settings, value) => {
  const options = await fetchRemoteOptionsFromSettings({ ...settings, query: value });
  const option = options.find((opt) => opt.value == value) || {
    value,
    label: JSON.stringify(value),
  };
  return { value: option, label: option.label };
};

const getRemoteOptionsTuples = async (settings, values) => {
  const options = await Promise.all(values.map((value) => getRemoteOptionTuple(settings, value)));

  return { value: options, label: options.map((opt) => opt.label).join(', ') };
};

const AdvancedSelectField = ({
  name,
  label,
  information,
  placeholder,
  fieldHoC: FieldHoC = Field,
  required,
  validation = {},
  readonly,
  remoteOptions: remoteOptionsSettings,
  answersChoices,
  confidence,
  ...props
}) => {
  const id = useMemo(() => uuid(), [name]);
  const [query, setQuery] = useState(null);
  const [resolvedAnswerChoices, setResolvedAnswerChoices] = useState([]);
  const supplierID = useSelector((state) => get(state, 'task.currentTask.supplierId', null));
  const currentRemoteOptionsSettings = useMemo(
    () => ({
      ...remoteOptionsSettings,
      query,
      ...(remoteOptionsSettings?.addSupplierIDInHeaders && { supplierID }),
    }),
    [remoteOptionsSettings, query, supplierID],
  );
  const remoteOptionsState = useSelector(optionsStateSelector(currentRemoteOptionsSettings));
  const isCurrentQueryErrored = useSelector(optionsErrorSelector(currentRemoteOptionsSettings));
  const [fetchRemoteOptions] = useActions([fetchOptionsAction]);

  // We "debounce" query save in local state to avoid fetching for every new character
  const handleQueryChange = useMemo(
    () => debounce(setQuery, 400),
    [],
  );

  // Options can come from many places
  const options = useMemo(() => {
    if (remoteOptionsSettings && remoteOptionsState && remoteOptionsState.options) {
      return remoteOptionsState.options;
    }

    if (validation.options) {
      return validation.options;
    }
    if (validation.choices && validation.injectLabelValue) {
      let tempOptions = validation.choices.split('\n').map((choice) => ({
        label: choice,
        value: choice,
      }));
      tempOptions.forEach((elem) => {
        let splitString = elem.label.split(validation.injectKeyValueSeparator);
        // eslint-disable-next-line no-param-reassign
        elem.label = splitString[0];
        // eslint-disable-next-line no-param-reassign
        elem.value = splitString[1];
      });
      return tempOptions;
    }

    if (validation.choices) {
      return validation.choices.split('\n').map((choice) => ({
        label: choice,
        value: choice,
      }));
    }

    return [];
  }, [remoteOptionsState, validation]);

  useEffect(() => {
    // We fetch list options if :
    //   - remoteOptions is provided
    //   - there is a query and it satisfies the minimum characters setting OR we are not
    // in "query mode" (no search query allowed)
    if (
      remoteOptionsSettings &&
      remoteOptionsSettings.url &&
      (
        (
          query &&
          (!remoteOptionsSettings.minChars || query.length >= remoteOptionsSettings.minChars)
        ) || !remoteOptionsSettings?.queryMode
      )
    ) {
      // We add a unique id to use if query needs to be cancelled (if data is
      // already in reducer)
      fetchRemoteOptions(currentRemoteOptionsSettings, uuid());
    }
  }, [currentRemoteOptionsSettings]);

  const handleFirstChoicesOpen = useCallback(async () => {
    if (!validation.remoteOptionsEnabled) {
      setResolvedAnswerChoices(answersChoices);
    } else {
      const finalRemoteOptionsSettings = {
        ...remoteOptionsSettings,
        ...(remoteOptionsSettings.addSupplierIDInHeaders && { supplierID }),
      };
      const remoteAnswersChoices = await Promise.all(
        answersChoices.map(({ value }) =>
          !validation.multi
            ? getRemoteOptionTuple(finalRemoteOptionsSettings, value)
            : getRemoteOptionsTuples(finalRemoteOptionsSettings, value),
        ),
      );
      setResolvedAnswerChoices(remoteAnswersChoices);
    }
  }, [answersChoices]);

  return (
    information,
    (
      <FormField
        label={label}
        name={name}
        information={information}
        htmlFor={id}
        required={required}
        verticalAlign={true}
        multi={validation.multi}
        answersChoices={resolvedAnswerChoices}
        forceAnswersChoicesCaret={!!answersChoices}
        onFirstChoicesOpen={handleFirstChoicesOpen}
        confidence={confidence}
      >
        <FieldHoC
          name={name}
          component={AdvancedSelectFieldRender}
          options={options}
          multi={validation.multi}
          autoselect={validation.autoselect}
          placeholder={placeholder}
          readonly={readonly}
          loading={remoteOptionsState?.pending}
          isCurrentQueryErrored={isCurrentQueryErrored}
          onInputChange={remoteOptionsSettings?.queryMode ? handleQueryChange : null}
          queryMode={remoteOptionsSettings?.queryMode}
          remoteMode={validation.remoteOptionsEnabled}
          minChars={remoteOptionsSettings?.minChars}
          confidence={confidence}
          {...props}
        />
      </FormField>
    )
  );
};

AdvancedSelectField.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  information: PropTypes.string,
  placeholder: PropTypes.string,
  multi: PropTypes.bool,
  fieldHoC: PropTypes.func,
  required: PropTypes.bool,
  validation: PropTypes.object,
  readonly: PropTypes.bool,
  remoteOptions: PropTypes.object,
  answersChoices: PropTypes.array,
  confidence: PropTypes.number,
};

export default AdvancedSelectField;
